/**
 * Linear equation system and matrix decomposition related tools.
 *
 * Based on:
 * https://github.com/sloisel/numeric

Numeric Javascript
Copyright (C) 2011 by Sébastien Loisel

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
 */

interface LuPermutation {
  lu: number[][];
  p: number[]; // Permutation vector
}

/**
 * Perform LUP decomposition of the matrix A. Allowed to mutate matrix A.
 * @param A
 * @returns
 */
function LU(A): LuPermutation {
  const abs = Math.abs;
  let i, j, k, absAjk, Akk, Ak, Pk, Ai;
  let max;
  const n = A.length,
    n1 = n - 1;
  const P: number[] = new Array(n);

  for (k = 0; k < n; ++k) {
    Pk = k;
    Ak = A[k];
    max = abs(Ak[k]);
    for (j = k + 1; j < n; ++j) {
      absAjk = abs(A[j][k]);
      if (max < absAjk) {
        max = absAjk;
        Pk = j;
      }
    }
    P[k] = Pk;

    if (Pk !== k) {
      A[k] = A[Pk];
      A[Pk] = Ak;
      Ak = A[k];
    }

    Akk = Ak[k];

    for (i = k + 1; i < n; ++i) {
      A[i][k] /= Akk;
    }

    for (i = k + 1; i < n; ++i) {
      Ai = A[i];
      for (j = k + 1; j < n1; ++j) {
        Ai[j] -= Ai[k] * Ak[j];
        ++j;
        Ai[j] -= Ai[k] * Ak[j];
      }
      if (j === n1) {
        Ai[j] -= Ai[k] * Ak[j];
      }
    }
  }

  return {
    lu: A,
    p: P,
  };
}

/**
 * Solve a set of linear equations based on the LUP decomposition of the parameter matrix
 * @param LUP
 * @param b
 * @returns
 */
function LUsolve(LUP: LuPermutation, b: number[]): number[] {
  const { lu: LU, p: P } = LUP;
  const n = LU.length;
  const x = [...b];
  let Pi, LUi, tmp;

  for (let i = 0; i < n; ++i) {
    Pi = P[i];
    if (P[i] !== i) {
      tmp = x[i];
      x[i] = x[Pi];
      x[Pi] = tmp;
    }

    LUi = LU[i];
    for (let j = 0; j < i; ++j) {
      x[i] -= x[j] * LUi[j];
    }
  }

  for (let i = n - 1; i >= 0; --i) {
    LUi = LU[i];
    for (let j = i + 1; j < n; ++j) {
      x[i] -= x[j] * LUi[j];
    }

    x[i] /= LUi[i];
  }

  return x;
}

/**
 * Solve a set of linear equations encoded as a matrix.
 * Should return the least squares solution.
 * @param A Matrix with the coefficients. Can be mutated by this function!
 * @param b The other side of the linear equation system.
 * @returns Solution for the linear equation system as a vector.
 */
export function solveLinearEquationSystem(A: number[][], b: number[]): number[] {
  return LUsolve(LU(A), b);
}
